import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { SEO } from '@/components/SEO';
import { VehicleCard } from '@/components/vehicles/VehicleCard';
import { getVehicles, getVehicleFilters } from '@/lib/api';
import { SlidersHorizontal, X, ChevronLeft, ChevronRight, Search, Car } from 'lucide-react';

export default function ListingsPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [vehicles, setVehicles] = useState([]);
    const [filterOptions, setFilterOptions] = useState({});
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState(1);
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

    const currentPage = parseInt(searchParams.get('page') || '1');
    const currentSort = searchParams.get('sort') || 'newest';
    const currentBrand = searchParams.get('brand') || '';
    const currentFuel = searchParams.get('fuel') || '';
    const currentPriceMax = searchParams.get('price_max') || '';
    const currentPriceMin = searchParams.get('price_min') || '';
    const currentYearMin = searchParams.get('year_min') || '';
    const currentKmMax = searchParams.get('km_max') || '';
    const currentSearch = searchParams.get('search') || '';

    const activeFilterCount = [currentBrand, currentFuel, currentPriceMax, currentPriceMin, currentYearMin, currentKmMax, currentSearch].filter(Boolean).length;

    const fetchVehicles = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page: currentPage, limit: 12, sort: currentSort };
            if (currentBrand && currentBrand !== 'all') params.brand = currentBrand;
            if (currentFuel && currentFuel !== 'all') params.fuel = currentFuel;
            if (currentPriceMax) params.price_max = parseFloat(currentPriceMax);
            if (currentPriceMin) params.price_min = parseFloat(currentPriceMin);
            if (currentYearMin) params.year_min = parseInt(currentYearMin);
            if (currentKmMax) params.km_max = parseInt(currentKmMax);
            if (currentSearch) params.search = currentSearch;
            const res = await getVehicles(params);
            setVehicles(res.data.vehicles || []);
            setTotal(res.data.total || 0);
            setPages(res.data.pages || 1);
        } catch (e) {
            console.error('Error fetching vehicles:', e);
        } finally {
            setLoading(false);
        }
    }, [currentPage, currentSort, currentBrand, currentFuel, currentPriceMax, currentPriceMin, currentYearMin, currentKmMax, currentSearch]);

    useEffect(() => {
        fetchVehicles();
    }, [fetchVehicles]);

    useEffect(() => {
        getVehicleFilters().then(res => setFilterOptions(res.data)).catch(() => {});
    }, []);

    const updateFilter = (key, value) => {
        const newParams = new URLSearchParams(searchParams);
        if (value && value !== 'all') {
            newParams.set(key, value);
        } else {
            newParams.delete(key);
        }
        newParams.set('page', '1');
        setSearchParams(newParams);
    };

    const clearFilters = () => {
        setSearchParams({ sort: currentSort });
    };

    const goToPage = (p) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('page', String(p));
        setSearchParams(newParams);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const activeChips = [];
    if (currentBrand && currentBrand !== 'all') activeChips.push({ key: 'brand', label: currentBrand });
    if (currentFuel && currentFuel !== 'all') activeChips.push({ key: 'fuel', label: currentFuel });
    if (currentPriceMax) activeChips.push({ key: 'price_max', label: `Max ${parseInt(currentPriceMax).toLocaleString('es-ES')}€` });
    if (currentPriceMin) activeChips.push({ key: 'price_min', label: `Min ${parseInt(currentPriceMin).toLocaleString('es-ES')}€` });
    if (currentYearMin) activeChips.push({ key: 'year_min', label: `Desde ${currentYearMin}` });
    if (currentKmMax) activeChips.push({ key: 'km_max', label: `Max ${parseInt(currentKmMax).toLocaleString('es-ES')} km` });
    if (currentSearch) activeChips.push({ key: 'search', label: `"${currentSearch}"` });

    const FilterControls = () => (
        <div className="space-y-4">
            <div>
                <label className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wide mb-1.5 block">Buscar</label>
                <Input
                    placeholder="Marca, modelo..."
                    value={currentSearch}
                    onChange={(e) => updateFilter('search', e.target.value)}
                    className="h-9"
                    data-testid="filter-search-input"
                />
            </div>
            <Accordion type="multiple" defaultValue={['brand', 'price', 'year']} className="space-y-0">
                <AccordionItem value="brand">
                    <AccordionTrigger className="text-sm font-medium py-2">Marca</AccordionTrigger>
                    <AccordionContent>
                        <Select value={currentBrand || 'all'} onValueChange={(v) => updateFilter('brand', v)}>
                            <SelectTrigger className="h-9" data-testid="filter-brand-select">
                                <SelectValue placeholder="Todas" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todas las marcas</SelectItem>
                                {(filterOptions?.brands || []).map(b => (
                                    <SelectItem key={b} value={b}>{b}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </AccordionContent>
                </AccordionItem>
                <AccordionItem value="fuel">
                    <AccordionTrigger className="text-sm font-medium py-2">Combustible</AccordionTrigger>
                    <AccordionContent>
                        <Select value={currentFuel || 'all'} onValueChange={(v) => updateFilter('fuel', v)}>
                            <SelectTrigger className="h-9" data-testid="filter-fuel-select">
                                <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos</SelectItem>
                                {(filterOptions?.fuels || []).map(f => (
                                    <SelectItem key={f} value={f}>{f}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </AccordionContent>
                </AccordionItem>
                <AccordionItem value="price">
                    <AccordionTrigger className="text-sm font-medium py-2">Precio</AccordionTrigger>
                    <AccordionContent>
                        <div className="flex gap-2">
                            <Input type="number" placeholder="Mín €" value={currentPriceMin} onChange={(e) => updateFilter('price_min', e.target.value)} className="h-9" data-testid="filter-price-min" />
                            <Input type="number" placeholder="Máx €" value={currentPriceMax} onChange={(e) => updateFilter('price_max', e.target.value)} className="h-9" data-testid="filter-price-max" />
                        </div>
                    </AccordionContent>
                </AccordionItem>
                <AccordionItem value="year">
                    <AccordionTrigger className="text-sm font-medium py-2">Año desde</AccordionTrigger>
                    <AccordionContent>
                        <Input type="number" placeholder="Ej: 2018" value={currentYearMin} onChange={(e) => updateFilter('year_min', e.target.value)} className="h-9" data-testid="filter-year-min" />
                    </AccordionContent>
                </AccordionItem>
                <AccordionItem value="km">
                    <AccordionTrigger className="text-sm font-medium py-2">Kilómetros máx.</AccordionTrigger>
                    <AccordionContent>
                        <Input type="number" placeholder="Ej: 100000" value={currentKmMax} onChange={(e) => updateFilter('km_max', e.target.value)} className="h-9" data-testid="filter-km-max" />
                    </AccordionContent>
                </AccordionItem>
            </Accordion>
            {activeFilterCount > 0 && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="w-full text-[hsl(var(--muted-foreground))]" data-testid="listing-clear-filters-button">
                    <X className="h-3.5 w-3.5 mr-1" /> Limpiar filtros
                </Button>
            )}
        </div>
    );

    return (
        <div className="py-6 sm:py-8" data-testid="listings-page">
            <SEO
                title="Coches de Segunda Mano - Todos los Vehículos"
                description={`${total} vehículos de ocasión disponibles en ASG Motor, Don Benito. Coches revisados con garantía, financiación y entrega a domicilio.`}
                path="/coches"
            />
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                {/* Top bar */}
                <div className="flex items-center justify-between mb-5 gap-3">
                    <h1 className="font-[var(--font-display)] tracking-tight text-2xl sm:text-3xl font-semibold">
                        Vehículos
                    </h1>
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="listing-results-count">
                            {total} resultado{total !== 1 ? 's' : ''}
                        </span>
                    </div>
                </div>

                {/* Sticky sort bar + mobile filter button */}
                <div className="sticky top-16 z-30 bg-[hsl(var(--background))]/90 backdrop-blur-md border-b -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 mb-5 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
                            <SheetTrigger asChild className="lg:hidden">
                                <Button variant="outline" size="sm" className="gap-1.5" data-testid="listing-open-filters-button">
                                    <SlidersHorizontal className="h-3.5 w-3.5" />
                                    Filtrar
                                    {activeFilterCount > 0 && (
                                        <Badge className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-[10px] bg-[var(--brand-blue)] text-white">
                                            {activeFilterCount}
                                        </Badge>
                                    )}
                                </Button>
                            </SheetTrigger>
                            <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-2xl">
                                <SheetHeader>
                                    <SheetTitle>Filtros</SheetTitle>
                                </SheetHeader>
                                <div className="py-4">
                                    <FilterControls />
                                </div>
                                <SheetFooter>
                                    <Button onClick={() => setMobileFiltersOpen(false)} className="w-full bg-[var(--brand-navy)] text-white" data-testid="listing-apply-filters-button">
                                        Ver {total} resultado{total !== 1 ? 's' : ''}
                                    </Button>
                                </SheetFooter>
                            </SheetContent>
                        </Sheet>
                    </div>
                    <Select value={currentSort} onValueChange={(v) => updateFilter('sort', v)}>
                        <SelectTrigger className="w-[180px] h-9" data-testid="listing-sort-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="newest">Más recientes</SelectItem>
                            <SelectItem value="price_asc">Precio: menor a mayor</SelectItem>
                            <SelectItem value="price_desc">Precio: mayor a menor</SelectItem>
                            <SelectItem value="km_asc">Menos kilómetros</SelectItem>
                            <SelectItem value="year_desc">Más nuevos</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Active chips */}
                {activeChips.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                        {activeChips.map(chip => (
                            <Badge key={chip.key} variant="secondary" className="gap-1 pl-2.5 pr-1.5 py-1 cursor-pointer hover:bg-[hsl(var(--secondary))]/80" onClick={() => updateFilter(chip.key, '')}>
                                {chip.label}
                                <X className="h-3 w-3 ml-0.5" />
                            </Badge>
                        ))}
                        <button onClick={clearFilters} className="text-xs text-[var(--brand-blue)] hover:underline font-medium">
                            Borrar todo
                        </button>
                    </div>
                )}

                <div className="flex gap-6">
                    {/* Desktop sidebar filters */}
                    <aside className="hidden lg:block w-72 shrink-0">
                        <Card className="p-4 sticky top-32 rounded-xl">
                            <h3 className="font-[var(--font-display)] font-semibold text-sm mb-3">Filtros</h3>
                            <FilterControls />
                        </Card>
                    </aside>

                    {/* Results grid */}
                    <div className="flex-1 min-w-0">
                        {loading ? (
                            <div className="grid gap-4 sm:gap-5 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3" data-testid="listing-vehicle-grid">
                                {Array.from({length: 6}).map((_, i) => (
                                    <Card key={i} className="overflow-hidden rounded-xl">
                                        <Skeleton className="aspect-[16/10] w-full" />
                                        <div className="p-4 space-y-3">
                                            <Skeleton className="h-4 w-3/4" />
                                            <Skeleton className="h-6 w-1/3" />
                                            <Skeleton className="h-3 w-full" />
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        ) : vehicles.length > 0 ? (
                            <>
                                <div className="grid gap-4 sm:gap-5 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3" data-testid="listing-vehicle-grid">
                                    {vehicles.map(v => <VehicleCard key={v.id} vehicle={v} />)}
                                </div>
                                {/* Pagination */}
                                {pages > 1 && (
                                    <div className="flex items-center justify-center gap-2 mt-8" data-testid="listing-pagination">
                                        <Button variant="outline" size="sm" onClick={() => goToPage(currentPage - 1)} disabled={currentPage <= 1}>
                                            <ChevronLeft className="h-4 w-4" />
                                        </Button>
                                        {Array.from({length: Math.min(pages, 7)}).map((_, i) => {
                                            const p = i + 1;
                                            return (
                                                <Button key={p} variant={p === currentPage ? 'default' : 'outline'} size="sm" onClick={() => goToPage(p)} className={p === currentPage ? 'bg-[var(--brand-navy)] text-white' : ''}>
                                                    {p}
                                                </Button>
                                            );
                                        })}
                                        <Button variant="outline" size="sm" onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= pages}>
                                            <ChevronRight className="h-4 w-4" />
                                        </Button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <Card className="p-8 text-center rounded-xl">
                                <Car className="h-12 w-12 mx-auto mb-3 text-[hsl(var(--muted-foreground))]/30" />
                                <h3 className="font-[var(--font-display)] font-semibold mb-2">No se encontraron vehículos</h3>
                                <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">Prueba ajustando los filtros.</p>
                                <Button variant="outline" onClick={clearFilters}>Limpiar filtros</Button>
                            </Card>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
